import * as Cl from '@design-system/component-library';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { cancelMsg, saveMsg, t } from '../../../common/i18n/index.js';
import { validateCallerDisplayName } from '../CallerDisplayHooks/callerDisplayNameValidation.js';
import {
  type CallerDisplayNameRowData,
  CallerDisplayPhoneNumberCell,
  WithCorporateSecondaryLine,
} from './CallerDisplayNameSettingRowView.js';
import type { CLTableRow } from '../../../common/types/componentLibrary';

interface CallerDisplayNameEditingValues {
  displayUser: string;
  displayCompany: string;
  corporateDisplayUser?: string;
  corporateDisplayCompany?: string;
}

const parseValue = (
  v: string | React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> | null
): string => {
  if (v == null) {
    return '';
  }
  if (typeof v === 'string') {
    return v;
  }
  return v.currentTarget.value;
};

const CorporateLabel = ({ pbxType }: { pbxType?: string }) => (
  <small className={dsClass.DISPLAY_BLOCK}>{pbxType === 'VAKIO' ? 'Vakio' : 'Ring'}:</small>
);

export const formatCallerDisplayNameRowBulkEdit = (
  row: CallerDisplayNameRowData,
  editValues: { user: string; owner: string; corporateUser?: string; corporateOwner?: string },
  onUserChange: (value: string) => void,
  onOwnerChange: (value: string) => void,
  onCorporateUserChange: (value: string) => void,
  onCorporateOwnerChange: (value: string) => void,
  searchQuery?: string
) =>
  ({
    cssclass: 'of-caller-display-name-row-edit',
    subscriptionLink: row.subscriptionDisplayId,
    phoneNumber: <CallerDisplayPhoneNumberCell row={row} searchQuery={searchQuery} />,
    activeDisplayName: (
      <WithCorporateSecondaryLine
        main={row.activeDisplayName}
        secondary={row.corporateNumber ? row.corporateActiveDisplayName : undefined}
        pbxType={row.pbxType}
      />
    ),
    originalDisplayName: (
      <WithCorporateSecondaryLine
        main={row.originalDisplayName}
        secondary={row.corporateNumber ? row.corporateOriginalDisplayName : undefined}
        pbxType={row.pbxType}
      />
    ),
    displayUser: (
      <div>
        {!!row.phoneNumber && (
          <Cl.Input
            className={dsClass.MARGIN_BOTTOM_0}
            value={editValues.user}
            i18n_input_errorMessage={validateCallerDisplayName(editValues.user)}
            onValueChange={v => onUserChange(parseValue(v))}
          />
        )}
        {row.corporateNumber && editValues.corporateUser !== undefined && (
          <div className={row.phoneNumber ? dsClass.MARGIN_TOP_2 : undefined}>
            <CorporateLabel pbxType={row.pbxType} />
            <Cl.Input
              className={dsClass.MARGIN_BOTTOM_0}
              value={editValues.corporateUser}
              i18n_input_errorMessage={validateCallerDisplayName(editValues.corporateUser)}
              onValueChange={v => onCorporateUserChange(parseValue(v))}
            />
          </div>
        )}
      </div>
    ),
    displayCompany: (
      <div>
        {!!row.phoneNumber && (
          <Cl.Input
            className={dsClass.MARGIN_BOTTOM_0}
            value={editValues.owner}
            i18n_input_errorMessage={validateCallerDisplayName(editValues.owner)}
            onValueChange={v => onOwnerChange(parseValue(v))}
          />
        )}
        {row.corporateNumber && editValues.corporateOwner !== undefined && (
          <div className={row.phoneNumber ? dsClass.MARGIN_TOP_2 : undefined}>
            <CorporateLabel pbxType={row.pbxType} />
            <Cl.Input
              className={dsClass.MARGIN_BOTTOM_0}
              value={editValues.corporateOwner}
              i18n_input_errorMessage={validateCallerDisplayName(editValues.corporateOwner)}
              onValueChange={v => onCorporateOwnerChange(parseValue(v))}
            />
          </div>
        )}
      </div>
    ),
    actionButtons: <></>,
  }) satisfies CLTableRow;

interface CallerDisplayNameRowEditCallbacks {
  onUserChange: (value: string) => void;
  onOwnerChange: (value: string) => void;
  onCorporateUserChange: (value: string) => void;
  onCorporateOwnerChange: (value: string) => void;
  onCancelEdit: () => void;
  onSubmitEdit: () => void;
}

export const formatCallerDisplayNameRowEdit = (
  row: CallerDisplayNameRowData,
  editingValues: CallerDisplayNameEditingValues,
  callbacks: CallerDisplayNameRowEditCallbacks,
  searchQuery?: string
) => {
  const { onUserChange, onOwnerChange, onCorporateUserChange, onCorporateOwnerChange, onCancelEdit, onSubmitEdit } =
    callbacks;
  return {
    ...formatCallerDisplayNameRowBulkEdit(
      row,
      {
        user: editingValues.displayUser,
        owner: editingValues.displayCompany,
        corporateUser: editingValues.corporateDisplayUser,
        corporateOwner: editingValues.corporateDisplayCompany,
      },
      onUserChange,
      onOwnerChange,
      onCorporateUserChange,
      onCorporateOwnerChange,
      searchQuery
    ),
    actionButtons: (
      <div
        key={`${row.id}-edit`}
        className={dsClass.DISPLAY_FLEX}
        style={{ flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}
      >
        <Cl.Button id="submit" onClick={onSubmitEdit}>
          {t.NIWE(saveMsg)}
        </Cl.Button>
        <Cl.Button color="link" onClick={onCancelEdit}>
          {t.B2V1(cancelMsg)}
        </Cl.Button>
      </div>
    ),
  } satisfies CLTableRow;
};
